/**
 * When something happened, with honest precision. An unknown time is never midnight and an
 * upload time is never an occurrence time.
 */
export type Precision = "minute" | "date" | "estimated_date" | "date_range" | "month" | "year" | "unknown";

export interface ObservedTime {
  value?: string;   // YYYY-MM-DD, YYYY-MM, YYYY or an ISO timestamp, matching precision
  precision: Precision;
  end?: string;     // for date_range
}

export const PRECISIONS: readonly Precision[] = ["minute", "date", "estimated_date", "date_range", "month", "year", "unknown"];

export function isValidObserved(t: ObservedTime): boolean {
  if (t.precision === "unknown") return !t.value && !t.end;
  if (!t.value) return false;
  switch (t.precision) {
    case "minute": return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(t.value);
    case "date":
    case "estimated_date": return /^\d{4}-\d{2}-\d{2}$/.test(t.value);
    case "date_range": return /^\d{4}-\d{2}-\d{2}$/.test(t.value) && !!t.end && /^\d{4}-\d{2}-\d{2}$/.test(t.end) && t.end >= t.value;
    case "month": return /^\d{4}-\d{2}$/.test(t.value);
    case "year": return /^\d{4}$/.test(t.value);
  }
}

/** A sortable key. Unknown times sort last; partial dates sort at the start of their period. */
export function sortKey(t: { value?: string | null; precision: Precision }): string {
  if (!t.value) return "9999";
  return t.value;
}

/** The calendar date (YYYY-MM-DD) in a timezone. */
export function localDate(at: Date, timeZone: string): string {
  const parts = new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(at);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  return `${get("year")}-${get("month")}-${get("day")}`;
}

export function addDays(date: string, days: number): string {
  const d = new Date(`${date.slice(0, 10)}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

export function daysBetween(from: string, to: string): number {
  const a = Date.parse(`${from.slice(0, 10)}T12:00:00Z`);
  const b = Date.parse(`${to.slice(0, 10)}T12:00:00Z`);
  return Math.round((b - a) / 86_400_000);
}

/** Age in whole days from a birth date to a reference date, or null when unknown. */
export function ageDays(birthDate: string | null | undefined, on: string): number | null {
  if (!birthDate) return null;
  const date = birthDate.length === 4 ? `${birthDate}-07-01` : birthDate.length === 7 ? `${birthDate}-15` : birthDate;
  return daysBetween(date, on);
}
